import React, { useMemo } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { CF } from "../theme/turquoiseTheme.js";
import { StreakHistoryChart } from "./StreakHistoryChart.js";
import {
  DASH,
  DonutGauge,
  HorizontalBarChart,
  MiniRingStat,
  TrendSparkline,
  WeekdayBarChart,
} from "./insight/InsightCharts.js";
import { useLanguage } from "../i18n/LanguageContext.js";

let WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function scoreForDay(entry) {
  if (!entry || typeof entry !== "object") return 0;
  const s = entry.status;
  if (s === "success") return 1;
  if (s === "partial") return 0.55;
  if (s === "relapse") return 0.15;
  if (s === "missed") return 0.25;
  return 0;
}

/** @param {Record<string, object>} markedDates @param {number} n */
function buildTrendPoints(markedDates, n = 14) {
  const points = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().split("T")[0];
    points.push({ key, score: scoreForDay(markedDates[key]) });
  }
  return points;
}

/** @param {Record<string, object>} markedDates */
function buildWeekdayRates(markedDates) {
  const totals = Array(7).fill(0);
  const weighted = Array(7).fill(0);
  for (const [key, entry] of Object.entries(markedDates || {})) {
    if (!entry || typeof entry !== "object") continue;
    const d = new Date(key);
    if (Number.isNaN(d.getTime())) continue;
    const wd = d.getDay();
    totals[wd] += 1;
    weighted[wd] += scoreForDay(entry);
  }
  return WEEKDAY_SHORT.map((label, i) => ({
    label,
    value: totals[i] > 0 ? Math.round((weighted[i] / totals[i]) * 100) : 0,
  }));
}

/**
 * @param {{
 *   habitName: string,
 *   insights: object,
 *   markedDates?: Record<string, object>,
 *   totalAttempts: number,
 *   currentStreak: number,
 *   goalDays: number,
 *   goalProgressPct: number,
 *   streakRuns: import('../utils/streakHistory.js').StreakRun[],
 * }} props
 */
export function InsightsTabPanel({
  habitName,
  insights,
  markedDates = {},
  totalAttempts,
  currentStreak,
  goalDays,
  goalProgressPct,
  streakRuns,
}) {
  const { t } = useLanguage();

  WEEKDAY_SHORT = [
    t("weekday_sunday"), t("weekday_monday"), t("weekday_tuesday"),
    t("weekday_wednesday"), t("weekday_thursday"), t("weekday_friday"), t("weekday_saturday"),
  ];

  const trendPoints = useMemo(() => buildTrendPoints(markedDates, 14), [markedDates]);
  const weekdayBars = useMemo(() => buildWeekdayRates(markedDates), [markedDates]);

  const relapseBars = useMemo(() => {
    if (!insights.hasRelapsePatternData || !insights.relapseTimeBreakdown) return [];
    return Object.entries(insights.relapseTimeBreakdown).map(([label, value]) => ({ label, value }));
  }, [insights]);

  const avgTrend = useMemo(() => {
    if (trendPoints.length === 0) return 0;
    const sum = trendPoints.reduce((a, p) => a + p.score, 0);
    return Math.round((sum / trendPoints.length) * 100);
  }, [trendPoints]);

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
      <View style={styles.header}>
        <Text style={styles.dashboardTitle}>{t("insight_title")}</Text>
        <Text style={styles.dashboardSub}>{habitName}</Text>
        <View style={styles.filterRow}>
          <View style={[styles.filterPill, styles.filterPillOn]}>
            <Text style={[styles.filterText, styles.filterTextOn]}>14D</Text>
          </View>
          <View style={styles.filterPill}>
            <Text style={styles.filterText}>30D</Text>
          </View>
          <View style={styles.filterPill}>
            <Text style={styles.filterText}>ALL</Text>
          </View>
          <Text style={styles.trackedBadge}>
            {insights.totalTracked} {t("insight_tracked_days")}
          </Text>
        </View>
      </View>

      {/* Hero row — trend + KPI gradient */}
      <View style={styles.heroRow}>
        <View style={[styles.card, styles.heroChart]}>
          <Text style={styles.cardTitle}>{t("insight_consistency")}</Text>
          <Text style={styles.cardMetric}>
            {avgTrend}%
            <Text style={styles.cardMetricSub}> {t("insight_avg_14")}</Text>
          </Text>
          <TrendSparkline points={trendPoints} />
          <View style={styles.legendRow}>
            <LegendChip color={CF.primaryBright} label={t("insight_success")} />
            <LegendChip color={CF.cyan} label={t("insight_partial")} />
            <LegendChip color="rgba(148,163,184,0.5)" label={t("insight_gap")} />
          </View>
        </View>

        <View style={styles.heroRight}>
          <View style={styles.gradientCard}>
            <Text style={styles.gradientLabel}>{t("insight_active_streak")}</Text>
            <Text style={styles.gradientValue}>{currentStreak}</Text>
            <Text style={styles.gradientUnit}>{t("home_days").toLowerCase()}</Text>
            <View style={styles.gradientDivider} />
            <Text style={styles.gradientLabel}>{t("insight_best_run")}</Text>
            <Text style={styles.gradientValueSm}>{insights.bestStreak}d</Text>
          </View>
        </View>
      </View>

      {/* ROI-style donut + goal */}
      <View style={styles.midRow}>
        <View style={[styles.card, styles.donutCard]}>
          <Text style={styles.cardTitle}>{t("insight_success_rate")}</Text>
          <View style={styles.donutRow}>
            <DonutGauge percent={insights.successRate} size={108} stroke={11}>
              <Text style={styles.donutPct}>{insights.successRate}%</Text>
            </DonutGauge>
            <View style={styles.donutMeta}>
              <Text style={styles.donutMetaLabel}>{t("insight_goal_progress")}</Text>
              <Text style={styles.donutMetaValue}>
                {currentStreak}/{goalDays} {t("home_days").toLowerCase()}
              </Text>
              <View style={styles.goalTrack}>
                <View style={[styles.goalFill, { width: `${goalProgressPct}%` }]} />
              </View>
              <Text style={styles.donutMetaHint}>{goalProgressPct}% {t("insight_of_goal").replace("{days}", goalDays)}</Text>
            </View>
          </View>
        </View>

        <View style={[styles.card, styles.compactCard]}>
          <Text style={styles.cardTitle}>{t("insight_recovery")}</Text>
          <Text style={styles.bigStat}>
            {insights.daysSinceLastRelapse === null ? "—" : insights.daysSinceLastRelapse}
          </Text>
          <Text style={styles.bigStatLabel}>{t("insight_days_since_fail")}</Text>
          <View style={styles.miniRings}>
            <View style={styles.miniRingItem}>
              <MiniRingStat value={insights.currentStreak} max={goalDays} color={CF.primaryBright} />
              <Text style={styles.miniRingLabel}>{t("insight_streak_label")}</Text>
            </View>
            <View style={styles.miniRingItem}>
              <MiniRingStat value={insights.bestStreak} max={Math.max(goalDays, insights.bestStreak)} color={CF.cyan} />
              <Text style={styles.miniRingLabel}>{t("insight_best_label")}</Text>
            </View>
          </View>
        </View>
      </View>

      {/* Metric grid */}
      <View style={styles.metricGrid}>
        <MetricTile label={t("insight_total_tracked")} value={String(insights.totalTracked)} />
        <MetricTile label={t("insight_attempts")} value={String(totalAttempts)} sub={t("insight_attempts_sub")} />
        <MetricTile
          label={t("insight_best_weekday")}
          value={
            insights.hasWeekdayData && insights.bestWeekday
              ? insights.bestWeekday.slice(0, 3)
              : "—"
          }
          sub={insights.bestWeekdayRate != null ? `${insights.bestWeekdayRate}%` : undefined}
        />
        <MetricTile
          label={t("insight_risk_day")}
          value={
            insights.hasWeekdayData && insights.worstWeekday
              ? insights.worstWeekday.slice(0, 3)
              : "—"
          }
          sub={insights.worstWeekdayRate != null ? `${insights.worstWeekdayRate}%` : undefined}
        />
      </View>

      {/* Weekday chart */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>{t("insight_weekday_perf")}</Text>
        <Text style={styles.cardSub}>{t("insight_weekday_sub")}</Text>
        <WeekdayBarChart data={weekdayBars} />
      </View>

      {/* Relapse patterns */}
      <View style={styles.splitRow}>
        <View style={[styles.card, styles.splitCard]}>
          <Text style={styles.cardTitle}>{t("insight_fail_time")}</Text>
          {relapseBars.length > 0 ? (
            <HorizontalBarChart data={relapseBars} height={relapseBars.length * 36} />
          ) : (
            <Text style={styles.emptyHint}>{t("insight_fail_time_hint")}</Text>
          )}
        </View>
        <View style={[styles.card, styles.splitCard]}>
          <Text style={styles.cardTitle}>{t("insight_top_trigger")}</Text>
          <Text style={styles.triggerValue}>
            {insights.hasRelapsePatternData && insights.mostCommonRelapseTrigger
              ? insights.mostCommonRelapseTrigger
              : "—"}
          </Text>
          <Text style={styles.cardSub}>{t("insight_common_relapse")}</Text>
          <Text style={styles.triggerTime}>{insights.mostCommonRelapseTimeOfDay}</Text>
        </View>
      </View>

      <View style={styles.historyWrap}>
        <StreakHistoryChart runs={streakRuns} variant="dashboard" />
      </View>
    </ScrollView>
  );
}

/** @param {{ color: string, label: string }} props */
function LegendChip({ color, label }) {
  return (
    <View style={styles.legendChip}>
      <View style={[styles.legendDot, { backgroundColor: color }]} />
      <Text style={styles.legendLabel}>{label}</Text>
    </View>
  );
}

/** @param {{ label: string, value: string, sub?: string }} props */
function MetricTile({ label, value, sub }) {
  return (
    <View style={styles.metricTile}>
      <Text style={styles.metricLabel}>{label}</Text>
      <Text style={styles.metricValue}>{value}</Text>
      {sub ? <Text style={styles.metricSub}>{sub}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1, backgroundColor: "transparent" },
  content: { padding: 14, paddingBottom: 28 },
  header: { marginBottom: 14 },
  dashboardTitle: { fontSize: 24, fontWeight: "900", color: DASH.text, letterSpacing: -0.5, ...DASH.textShadow },
  dashboardSub: { fontSize: 14, fontWeight: "700", color: CF.primaryBright, marginTop: 2, ...DASH.textShadowSoft },
  filterRow: { flexDirection: "row", alignItems: "center", gap: 8, marginTop: 12, flexWrap: "wrap" },
  filterPill: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 999,
    backgroundColor: "rgba(6, 12, 28, 0.32)",
    borderWidth: 1,
    borderColor: DASH.border,
  },
  filterPillOn: { backgroundColor: "rgba(13, 148, 136, 0.72)", borderColor: CF.primaryBright },
  filterText: { fontSize: 11, fontWeight: "800", color: DASH.textSoft, ...DASH.textShadowSoft },
  filterTextOn: { color: "#FFFFFF", ...DASH.textShadow },
  trackedBadge: { marginLeft: "auto", fontSize: 11, color: DASH.textMuted, fontWeight: "700", ...DASH.textShadowSoft },
  card: {
    backgroundColor: DASH.card,
    borderRadius: 18,
    padding: 14,
    borderWidth: 1,
    borderColor: DASH.border,
  },
  cardTitle: {
    fontSize: 12,
    fontWeight: "800",
    color: CF.primaryBright,
    letterSpacing: 0.5,
    marginBottom: 4,
    ...DASH.textShadowSoft,
  },
  cardSub: { fontSize: 11, color: DASH.textMuted, marginBottom: 10, fontWeight: "600", ...DASH.textShadowSoft },
  cardMetric: { fontSize: 22, fontWeight: "900", color: DASH.text, marginBottom: 8, ...DASH.textShadow },
  cardMetricSub: { fontSize: 13, fontWeight: "700", color: DASH.textSoft, ...DASH.textShadowSoft },
  heroRow: { flexDirection: "row", gap: 10, marginBottom: 10 },
  heroChart: { flex: 1.4 },
  heroRight: { flex: 0.9 },
  gradientCard: {
    flex: 1,
    borderRadius: 18,
    padding: 14,
    backgroundColor: "rgba(13, 148, 136, 0.5)",
    borderWidth: 1,
    borderColor: "rgba(94, 234, 212, 0.55)",
    justifyContent: "center",
    minHeight: 160,
  },
  gradientLabel: { fontSize: 10, fontWeight: "800", color: DASH.textSoft, letterSpacing: 0.8, ...DASH.textShadowSoft },
  gradientValue: { fontSize: 42, fontWeight: "900", color: "#FFFFFF", lineHeight: 46, ...DASH.textShadow },
  gradientUnit: { fontSize: 13, fontWeight: "700", color: DASH.textSoft, marginTop: -4, ...DASH.textShadowSoft },
  gradientDivider: { height: 1, backgroundColor: "rgba(255,255,255,0.28)", marginVertical: 10 },
  gradientValueSm: { fontSize: 22, fontWeight: "800", color: "#FFFFFF", ...DASH.textShadow },
  midRow: { flexDirection: "row", gap: 10, marginBottom: 10 },
  donutCard: { flex: 1.25 },
  compactCard: { flex: 0.85 },
  donutRow: { flexDirection: "row", alignItems: "center", gap: 12, marginTop: 4 },
  donutPct: { fontSize: 20, fontWeight: "900", color: DASH.text, ...DASH.textShadow },
  donutMeta: { flex: 1 },
  donutMetaLabel: { fontSize: 10, fontWeight: "700", color: DASH.textMuted, ...DASH.textShadowSoft },
  donutMetaValue: { fontSize: 16, fontWeight: "800", color: DASH.text, marginVertical: 4, ...DASH.textShadow },
  donutMetaHint: { fontSize: 10, color: DASH.textSoft, marginTop: 6, fontWeight: "600", ...DASH.textShadowSoft },
  goalTrack: {
    height: 6,
    borderRadius: 3,
    backgroundColor: "rgba(45, 212, 191, 0.25)",
    overflow: "hidden",
  },
  goalFill: { height: "100%", backgroundColor: CF.primaryBright, borderRadius: 3 },
  bigStat: { fontSize: 36, fontWeight: "900", color: DASH.text, marginTop: 4, ...DASH.textShadow },
  bigStatLabel: { fontSize: 11, color: DASH.textMuted, marginBottom: 10, fontWeight: "700", ...DASH.textShadowSoft },
  miniRings: { flexDirection: "row", justifyContent: "space-around" },
  miniRingItem: { alignItems: "center", gap: 4 },
  miniRingLabel: { fontSize: 9, fontWeight: "700", color: DASH.textSoft, ...DASH.textShadowSoft },
  metricGrid: { flexDirection: "row", flexWrap: "wrap", gap: 8, marginBottom: 10 },
  metricTile: {
    width: "48%",
    flexGrow: 1,
    minWidth: "46%",
    backgroundColor: DASH.cardElevated,
    borderRadius: 14,
    padding: 12,
    borderWidth: 1,
    borderColor: DASH.border,
  },
  metricLabel: {
    fontSize: 9,
    fontWeight: "800",
    color: DASH.textMuted,
    letterSpacing: 0.6,
    marginBottom: 6,
    ...DASH.textShadowSoft,
  },
  metricValue: { fontSize: 20, fontWeight: "900", color: DASH.text, ...DASH.textShadow },
  metricSub: { fontSize: 10, color: DASH.textSoft, marginTop: 2, fontWeight: "600", ...DASH.textShadowSoft },
  splitRow: { flexDirection: "row", gap: 10, marginBottom: 10 },
  splitCard: { flex: 1 },
  triggerValue: {
    fontSize: 15,
    fontWeight: "800",
    color: DASH.text,
    marginTop: 8,
    lineHeight: 20,
    ...DASH.textShadow,
  },
  triggerTime: { fontSize: 18, fontWeight: "900", color: CF.cyan, marginTop: 6, ...DASH.textShadow },
  emptyHint: { fontSize: 12, color: DASH.textSoft, fontStyle: "italic", marginTop: 12, ...DASH.textShadowSoft },
  legendRow: { flexDirection: "row", gap: 12, marginTop: 10 },
  legendChip: { flexDirection: "row", alignItems: "center", gap: 5 },
  legendDot: { width: 8, height: 8, borderRadius: 4 },
  legendLabel: { fontSize: 10, color: DASH.textSoft, fontWeight: "700", ...DASH.textShadowSoft },
  historyWrap: { marginTop: 4 },
});
